/**
 * Shared vocabulary for DrBell scheduling.
 *
 * Everything in this package is pure: no I/O, no ambient clock, no platform
 * APIs beyond `Intl`. Both the mobile device and the API Worker import these
 * functions, and they MUST agree exactly — a disagreement means the server
 * escalates to a caregiver while the patient's phone is still silent.
 */

/** ISO weekday: 1 = Monday ... 7 = Sunday. */
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** `YYYY-MM-DD`, interpreted in the patient's timezone. */
export type LocalDate = string;

/** `HH:MM` on a 24h clock, interpreted in the patient's timezone. */
export type LocalTime = string;

export type MealKey = 'breakfast' | 'lunch' | 'dinner' | 'bedtime';

export const MEAL_KEYS: readonly MealKey[] = ['breakfast', 'lunch', 'dinner', 'bedtime'];

/** The patient's own routine. `1-0-1` means different clock times for different people. */
export type MealTimes = Record<MealKey, LocalTime>;

/**
 * A person's own named time besides the meals — "Evening tea", "After walk"
 * (migration 0051, routine-times.ts). A medicine is tied to one exactly as
 * to a meal, by an anchor whose `meal` is `time:<id>`. The four meals stay
 * as they are: prescription notation (1-0-1, "after food") is about them.
 */
export type RoutineTimeKey = `time:${string}`;

/** What an anchor is tied to: one of the four meals, or one of the person's own times. */
export type AnchorKey = MealKey | RoutineTimeKey;

/** The clock time of every anchor a person has: the meals, and their own times by key. */
export type AnchorTimes = MealTimes & { [key: RoutineTimeKey]: LocalTime | undefined };

export const DEFAULT_MEAL_TIMES: MealTimes = {
  breakfast: '08:00',
  lunch: '13:00',
  dinner: '20:00',
  bedtime: '22:00',
};

export type FoodRelation =
  | 'before_food'
  | 'with_food'
  | 'after_food'
  | 'empty_stomach'
  | 'any';

export interface MealAnchor {
  /** A meal, or the person's own time (`time:<id>`). */
  meal: AnchorKey;
  /** Minutes relative to the meal. Negative = before, positive = after. */
  offsetMinutes: number;
  /**
   * Overrides the schedule's `doseQuantity` for this slot.
   * Indian notation needs this: `1-0-2` is one tablet in the morning, two at night.
   */
  doseQuantity?: number;
}

/**
 * Cycles apply on top of whatever produces times — 21 days on, 7 off, etc.
 * Modelled as a modifier rather than its own schedule type so that a cyclic
 * course can still be meal-relative.
 */
export interface CycleDef {
  onDays: number;
  offDays: number;
  startDate: LocalDate;
}

export interface ScheduleBase {
  id: string;
  /** Default quantity per dose; a `MealAnchor` may override it. */
  doseQuantity: number;
  doseUnit: string;
  foodRelation: FoodRelation;
  /** How long after `dueAt` the dose may still be taken. Drives `windowEnd`. */
  graceMinutes: number;
  /** How long before `dueAt` the dose may already be taken. Drives `windowStart`. */
  earlyMinutes: number;
  startDate: LocalDate;
  endDate?: LocalDate | null;
  /** Restrict to certain weekdays. `null`/absent = every day. */
  daysOfWeek?: Weekday[] | null;
  cycle?: CycleDef | null;
}

/** Explicit clock times. A wall-clock promise: 08:00 stays 08:00 across DST and travel. */
export interface FixedTimesSchedule extends ScheduleBase {
  type: 'fixed_times';
  times: LocalTime[];
}

/** Times derived from the patient's meals. The Indian-prescription case. */
export interface MealRelativeSchedule extends ScheduleBase {
  type: 'meal_relative';
  anchors: MealAnchor[];
}

/**
 * "Every N hours". Unlike `fixed_times`, this is a promise about *elapsed real
 * time*, so it deliberately drifts against the wall clock across a DST change.
 */
export interface IntervalSchedule extends ScheduleBase {
  type: 'interval';
  intervalHours: number;
  /** The first dose of the course, on `startDate`. */
  anchorTime: LocalTime;
}

/** As-needed. Never produces a scheduled occurrence, and never alarms. */
export interface PrnSchedule extends ScheduleBase {
  type: 'prn';
}

export type ScheduleDef =
  | FixedTimesSchedule
  | MealRelativeSchedule
  | IntervalSchedule
  | PrnSchedule;

export interface DoseOccurrence {
  scheduleId: string;
  /** Unix ms, UTC. The canonical instant. */
  dueAt: number;
  windowStart: number;
  windowEnd: number;
  doseQuantity: number;
  doseUnit: string;
  foodRelation: FoodRelation;
  /** Wall-clock rendering in the patient's timezone, for display and debugging. */
  localDate: LocalDate;
  localTime: LocalTime;
  /** Present for meal-relative schedules; lets the UI say "after breakfast" (or "after Evening tea"). */
  meal?: AnchorKey;
}

export interface MaterializeOptions {
  timeZone: string;
  /** The meals, and the person's own times by `time:<id>`. */
  mealTimes?: AnchorTimes;
  /** Inclusive lower bound (unix ms). */
  from: number;
  /** Exclusive upper bound (unix ms). */
  to: number;
  /**
   * Meal times on some weekdays (docs/33 C7): on `days` (1 = Monday) the
   * meals in `times` replace the usual ones; a meal left out keeps its time.
   */
  otherDays?: { days: readonly number[]; times: Partial<MealTimes> } | null;
}

/**
 * How loudly a reminder arrives — RX-REM-21.
 *
 * `alarm` takes the screen over and sounds through silent mode.
 * `notify` is an ordinary notification. `silent` shows it and makes no
 * sound, for the things worth remembering that nobody should be woken by.
 */
export type AlertStyle = 'alarm' | 'notify' | 'silent';
